import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../../lib/apiClient.js';

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
    </svg>
  );
}

function fmt(n) {
  return Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function StatCard({ label, value, sub, color = 'text-text' }) {
  return (
    <div className="card p-4">
      <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-1">{label}</p>
      <p className={`text-[20px] font-bold tabular-nums leading-tight ${color}`}>{value}</p>
      {sub && <p className="text-[11px] text-text-faint mt-0.5">{sub}</p>}
    </div>
  );
}

function Field({ label, value, empty = '—', fullWidth = false }) {
  return (
    <div className={fullWidth ? 'col-span-2' : ''}>
      <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-0.5">{label}</p>
      <p className="text-sm text-text">{value ?? empty}</p>
    </div>
  );
}

function Badge({ active, labelOn, labelOff }) {
  return active ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success/10 text-success border border-success/20">{labelOn}</span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-bg-s3 text-text-faint border border-border">{labelOff}</span>
  );
}

function Section({ title, children, action, noPad = false }) {
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-bg-s2">
        <h2 className="text-[13px] font-semibold text-text">{title}</h2>
        {action}
      </div>
      {noPad ? children : (
        <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-4">{children}</div>
      )}
    </div>
  );
}

function AssetClassBadge({ cls }) {
  const colors = {
    'Target Date':          'bg-blue-500/10 text-blue-600 border-blue-200',
    'US Large Cap Equity':  'bg-emerald-500/10 text-emerald-700 border-emerald-200',
    'US Mid/Small Cap Equity': 'bg-teal-500/10 text-teal-700 border-teal-200',
    'International Equity': 'bg-violet-500/10 text-violet-700 border-violet-200',
    'Fixed Income':         'bg-amber-500/10 text-amber-700 border-amber-200',
    'Stable Value':         'bg-slate-500/10 text-slate-600 border-slate-200',
    'Company Stock':        'bg-orange-500/10 text-orange-700 border-orange-200',
  };
  const c = colors[cls] || 'bg-bg-s3 text-text-faint border-border';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${c}`}>
      {cls}
    </span>
  );
}

export default function SponsorProfile() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiClient.getPlanProfile()
      .then((data) => setPlan(data))
      .catch((e) => setError(e.message || 'Failed to load plan profile'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {[1,2,3].map((i) => <div key={i} className="card p-4 h-20 animate-pulse bg-bg-s2" />)}
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="card p-5 space-y-3 animate-pulse">
            <div className="h-3 w-32 bg-bg-s3 rounded" />
            <div className="grid grid-cols-2 gap-4">
              {[1,2,3,4].map((j) => <div key={j} className="h-8 bg-bg-s3 rounded" />)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="card p-5 border-danger/30 bg-danger/5">
          <p className="text-sm text-danger">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">

      {/* Header */}
      <div className="card overflow-hidden flex">
        <div className="w-1 bg-accent flex-shrink-0" />
        <div className="p-5 flex-1">
          <h1 className="text-[20px] font-bold text-text leading-tight">{plan?.plan_name}</h1>
          <p className="text-sm text-text-muted mt-1">Plan profile, fund lineup, and configuration overview.</p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard
          label="Total Plan Assets"
          value={plan?.total_assets != null ? fmt(plan.total_assets) : '—'}
          sub="All participant balances"
          color="text-accent"
        />
        <StatCard
          label="Vested Assets"
          value={plan?.vested_assets != null ? fmt(plan.vested_assets) : '—'}
          sub="Participant-owned"
          color="text-success"
        />
        <StatCard
          label="Participants"
          value={plan?.participant_count ?? '—'}
          sub="Enrolled"
        />
        <StatCard
          label="Pending Reviews"
          value={plan?.pending_queue ?? 0}
          sub="Awaiting approval"
          color={plan?.pending_queue > 0 ? 'text-warning' : 'text-text'}
        />
      </div>

      {/* Form 5500 Export */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => apiClient.downloadForm5500().catch(() => {})}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-[12px] font-medium text-text-muted hover:bg-bg-s2 hover:text-text transition-colors"
        >
          <DownloadIcon />
          Export Form 5500 Data
        </button>
      </div>

      {/* Plan Identity */}
      <Section title="Plan Identity">
        <Field label="Plan ID" value={plan?.plan_id} />
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-0.5">ERISA Plan Number</p>
          <p className="text-sm text-text">{plan?.erisa_plan_number ?? '—'}</p>
          <p className="text-[10px] text-text-faint mt-0.5">3-digit Form 5500 plan number assigned per employer · "002" means this is the employer's second ERISA plan (e.g., plan #001 is a pension plan)</p>
        </div>
        <Field label="Plan Type" value={plan?.plan_type} />
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-0.5">Safe Harbor</p>
          <Badge active={plan?.safe_harbor} labelOn="Safe Harbor" labelOff="Traditional" />
        </div>
        <Field label="Effective Date" value={plan?.effective_date} />
        <Field label="Plan Year End" value={plan?.plan_year_end} />
      </Section>

      {/* Benefits & Vesting */}
      <Section
        title="Benefits & Vesting"
        action={
          <Link to="/sponsor/blackout" className="text-[11px] text-accent hover:text-accent-dark font-medium transition-colors">
            Blackout Manager →
          </Link>
        }
      >
        <Field label="Employer Match Formula" value={plan?.employer_match} fullWidth />
        <Field label="Vesting Schedule" value={plan?.vesting_schedule} fullWidth />
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-0.5">Loans Permitted</p>
          <Badge active={plan?.loans_permitted} labelOn="Yes" labelOff="No" />
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-0.5">Hardship Permitted</p>
          <Badge active={plan?.hardship_permitted} labelOn="Yes" labelOff="No" />
        </div>
        <div className="col-span-2">
          <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-0.5">Blackout Status</p>
          <Badge active={plan?.blackout_active} labelOn="Blackout Active" labelOff="No Active Blackout" />
        </div>
      </Section>

      {/* Fund Lineup */}
      <Section title={`Fund Lineup — ${plan?.fund_count ?? 0} funds`} noPad>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-bg-s3 border-b border-border">
                <th className="text-left font-mono font-normal text-text-faint px-4 py-2.5 whitespace-nowrap">Fund</th>
                <th className="text-left font-mono font-normal text-text-faint px-4 py-2.5 whitespace-nowrap">Ticker</th>
                <th className="text-left font-mono font-normal text-text-faint px-4 py-2.5 whitespace-nowrap">Asset Class</th>
                <th className="text-right font-mono font-normal text-text-faint px-4 py-2.5 whitespace-nowrap">Exp. Ratio</th>
                <th className="text-center font-mono font-normal text-text-faint px-4 py-2.5 whitespace-nowrap">QDIA</th>
              </tr>
            </thead>
            <tbody>
              {(plan?.funds ?? []).map((f) => (
                <tr key={f.fund_id} className="border-b border-border last:border-0 hover:bg-bg-s2 transition-colors">
                  <td className="px-4 py-2.5 text-text font-medium max-w-[220px]">
                    <span className="truncate block">{f.fund_name}</span>
                  </td>
                  <td className="px-4 py-2.5 text-text-muted font-mono">
                    {f.ticker || '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    <AssetClassBadge cls={f.asset_class} />
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-text-muted">
                    {f.expense_ratio != null ? `${(f.expense_ratio * 100).toFixed(2)}%` : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    {f.is_qdia ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success/10 text-success border border-success/20">QDIA</span>
                    ) : (
                      <span className="text-text-faint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-border bg-bg-s2">
          <p className="text-[10px] text-text-faint font-mono">
            QDIA = Qualified Default Investment Alternative (ERISA §404(c)(5)) · Fiduciary-reviewed fund lineup
          </p>
        </div>
      </Section>

      <p className="text-[11px] text-text-faint text-center font-mono pb-2">
        Plan data is read-only · ERISA §107 · IRC §401(k) · DOL oversight applies
      </p>
    </div>
  );
}
